import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { COURSES } from "@/constants/testIds";
import { ChevronLeft, ChevronRight, Lock, PlayCircle } from "lucide-react";
import CourseSearch from "@/components/CourseSearch";

const PAGE_SIZE = 6;

export default function Courses() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [courses, setCourses] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get(`/courses?language=${lang}`).then((r) => setCourses(r.data)).catch(() => {});
    // Reset to first page when the catalog changes (new language). Prevents
    // "I was on page 4 but now there are only 2 pages" ghost-state.
    setPage(1);
  }, [lang]);

  // Free members can browse the full listing — paid courses show a lock
  // badge and hit the upsell inside `CourseDetail`. Admins on any tier
  // always see the real page.
  //
  // Only unauthenticated visitors (`user === null`) still fall through
  // to `/preview` via the top-level `PrivateRoute` guard.
  const TIER_RANK = { free: 0, basic: 1, premium: 2 };
  const viewerRank = user?.role === "admin"
    ? 99
    : (TIER_RANK[user?.tier || "free"] ?? 0);

  const totalPages = Math.max(1, Math.ceil(courses.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageSlice = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return courses.slice(start, start + PAGE_SIZE);
  }, [courses, safePage]);

  return (
    <div data-testid={COURSES.root} className="bg-[#F8FAFC]">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
        <div className="max-w-3xl mb-10">
          <div className="text-xs uppercase tracking-[0.28em] text-blue-800 font-semibold mb-3">{t("coursesUi.eyebrow")}</div>
          <h1 className="font-display font-black text-5xl text-slate-900 leading-tight tracking-tighter">{t("coursesUi.title")}</h1>
          <p className="mt-4 text-slate-600 text-lg">{t("coursesUi.subtitle")}</p>
        </div>

        <div className="mb-14">
          <CourseSearch />
        </div>

        <div className="flex flex-col gap-5">
          {pageSlice.map((c) => {
            const courseRank = TIER_RANK[c.tier] ?? 0;
            const locked = courseRank > viewerRank;
            const Wrapper = locked ? "div" : Link;
            const wrapperProps = locked
              ? { className: "group flex flex-col sm:flex-row bg-slate-100 border border-slate-200 rounded-2xl overflow-hidden opacity-70 cursor-not-allowed relative" }
              : { to: `/courses/${c.id}`, className: "group flex flex-col sm:flex-row bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 hover:border-blue-300 transition-all duration-300" };
            return (
              <Wrapper
                key={c.id}
                data-testid={COURSES.card}
                data-locked={locked ? "true" : "false"}
                {...wrapperProps}
              >
                {/* Banner — fixed landscape proportion on sm+ so wide
                    cover art never gets cropped vertically to match a
                    long description. Stacks on top on mobile. */}
                <div className={`relative shrink-0 w-full sm:w-72 md:w-80 aspect-[16/9] sm:aspect-auto sm:self-stretch overflow-hidden bg-slate-100 ${locked ? "grayscale" : ""}`}>
                  {c.cover_image ? (
                    <img
                      src={c.cover_image}
                      alt=""
                      className={`w-full h-full object-cover ${locked ? "" : "group-hover:scale-[1.02] transition-transform duration-500"}`}
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs uppercase tracking-widest">
                      {t("coursesUi.noCover")}
                    </div>
                  )}
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 bg-white/90 backdrop-blur px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-semibold text-slate-900">
                    {c.tier !== "free" && <Lock size={10} />}
                    {t(`tier.${c.tier}`)}
                  </div>
                </div>

                {/* Body — grows to fill the row, caps summary at 3 lines */}
                <div className="flex-1 min-w-0 p-5 sm:p-6 flex flex-col justify-center gap-2">
                  <div className="text-[10px] uppercase tracking-widest text-blue-800 font-semibold">
                    {c.level}{c.duration ? ` · ${c.duration}` : ""}
                  </div>
                  <h3 className={`font-display font-bold text-xl leading-snug tracking-tight ${locked ? "text-slate-500" : "text-slate-900 group-hover:text-blue-800"}`}>
                    {c.title}
                  </h3>
                  <p className={`text-sm leading-relaxed line-clamp-3 ${locked ? "text-slate-500" : "text-slate-600"}`}>
                    {c.summary}
                  </p>
                  <div className="mt-1">
                    {locked ? (
                      <Link
                        to="/pricing"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="courses-upgrade-cta"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:text-amber-800 underline decoration-amber-400 underline-offset-4 pointer-events-auto"
                      >
                        <Lock size={14} /> {t("coursesUi.upgradeToUnlock")}
                      </Link>
                    ) : (
                      <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-800 group-hover:text-blue-700">
                        <PlayCircle size={16} /> {t("coursesUi.viewCourse")}
                      </div>
                    )}
                  </div>
                </div>
              </Wrapper>
            );
          })}
        </div>

        {totalPages > 1 && (
          <nav
            data-testid="courses-pagination"
            aria-label={t("coursesUi.paginationLabel")}
            className="mt-10 flex items-center justify-between gap-4"
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-white hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="courses-pagination-prev"
            >
              <ChevronLeft size={14} /> {t("coursesUi.previous")}
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  aria-current={n === safePage ? "page" : undefined}
                  className={`min-w-9 h-9 px-3 text-sm font-semibold rounded-lg transition-colors ${
                    n === safePage
                      ? "bg-blue-800 text-white"
                      : "text-slate-600 hover:bg-white hover:text-blue-800"
                  }`}
                  data-testid={`courses-pagination-page-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-white hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="courses-pagination-next"
            >
              {t("coursesUi.next")} <ChevronRight size={14} />
            </button>
          </nav>
        )}
      </section>
    </div>
  );
}
